import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu,
  X,
  Sparkles,
  Phone,
  ArrowRight,
  ArrowDown,
  Star,
  Shield,
  Zap,
  Heart,
  Repeat,
  ChevronDown,
} from 'lucide-react';
import MomshiachMethodCheckout from '../components/MomshiachMethodCheckout';
import { testimonials } from '../mock';

const Method = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('full');
  const [openFaq, setOpenFaq] = useState(null);

  const openCheckout = (plan = 'full') => {
    setSelectedPlan(plan);
    setCheckoutOpen(true);
  };

  const scrollToId = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const faqs = [
    {
      q: 'Do I have to be present for the energy therapy sessions?',
      a: "No — that's the whole point. The 6 Energy Therapy sessions are remote and passive. I do the work while you're at the grocery store, on the school run, or in bed. You get a voice note of what came up in each session.",
    },
    {
      q: "I've done therapy for years and still feel stuck. Why would this be different?",
      a: "Talk therapy works with your story. This works with your body and your nervous system. Energy Therapy, EFT, and gentle plant-forward nutrition move things at a physiological level — before the mind can talk you out of them. That's why it holds when other things haven't.",
    },
    {
      q: 'What if I can\u2019t afford the full amount?',
      a: 'That\u2019s exactly why the payment plans exist. Split it into 3 or 6 months — whichever gives you the softest landing. If even that\u2019s hard right now, message me on WhatsApp and we\u2019ll figure it out.',
    },
    {
      q: 'How long does the whole method take?',
      a: 'The 6 Energy Therapy sessions run weekly for 6 weeks. Health Coaching and EFT can be spaced to fit your life. Most moms complete the whole method within 3–4 months.',
    },
    {
      q: 'Is this kosher/hasgacha-approved?',
      a: 'Yes. I was trained under Rebbetzin Orit Esther Riter and my energy work carries hasgacha from Rabbi Ben-Tzion Wosner shlit"a. Everything I offer is grounded in Torah values.',
    },
  ];

  return (
    <div className="landing-page mm-landing">
      {checkoutOpen && (
        <MomshiachMethodCheckout initialPlan={selectedPlan} onClose={() => setCheckoutOpen(false)} />
      )}

      {/* Header */}
      <header className="header-nav">
        <div className="header-content">
          <Link to="/" data-testid="mm-logo-link">
            <img src="/brand/logo.png" alt="Momshiach — Your Personal Redemption" className="logo-img" data-testid="header-logo" />
          </Link>
          <nav className="desktop-nav">
            <Link to="/" className="nav-link" data-testid="mm-nav-home">Home</Link>
            <Link to="/energy-therapy" className="nav-link" data-testid="nav-energy">Energy Therapy</Link>
            <Link to="/carebnb" className="nav-link" data-testid="nav-carebnb">CareBnB</Link>
            <Link to="/health-coaching" className="nav-link" data-testid="nav-health-coaching">Health Coaching</Link>
            <Link to="/resources" className="nav-link" data-testid="nav-resources">Resources</Link>
          </nav>
          <div className="nav-actions">
            <a href="https://wa.me/972529326458" target="_blank" rel="noopener noreferrer" className="btn-secondary btn-contact" data-testid="header-contact-btn">
              <Phone size={16} />
              <span>Contact</span>
            </a>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="mobile-menu-btn">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <Link to="/" className="mobile-nav-link" data-testid="mm-mobile-nav-home">Home</Link>
            <Link to="/energy-therapy" className="mobile-nav-link">Energy Therapy</Link>
            <Link to="/carebnb" className="mobile-nav-link">CareBnB</Link>
            <Link to="/health-coaching" className="mobile-nav-link">Health Coaching</Link>
            <Link to="/resources" className="mobile-nav-link">Resources</Link>
            <Link to="/gift" className="mobile-nav-link">Gift a Getaway</Link>
          </div>
        )}
      </header>

      {/* Hero — Identity Statement */}
      <section className="mm-hero" data-testid="mm-hero">
        <div className="container">
          <span className="mm-eyebrow">
            <Sparkles size={14} /> The Momshiach Method™
          </span>
          <h1 className="mm-hero-title">
            Come home <br />
            <em>to yourself.</em>
          </h1>
          <p className="mm-hero-sub">
            You&apos;ve been the mom, the wife, the friend, the fixer — for years.<br />
            Somewhere in there, you got lost. Even in your own body.<br />
            This is the way back.
          </p>
          <div className="mm-hero-cta">
            <a href="#the-method" onClick={scrollToId('the-method')} className="btn-primary btn-lg" data-testid="hero-primary-cta">
              <span>See The Method</span>
              <ArrowDown size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Identification — "I've been there" */}
      <section className="mm-identify" data-testid="mm-identify">
        <div className="container mm-narrow">
          <span className="mm-eyebrow"><Heart size={14} /> I See You</span>
          <h2 className="mm-h2">I&apos;ve been there.</h2>
          <div className="mm-identify-body">
            <p>You&apos;ve tried the diets.</p>
            <p>You&apos;ve done the therapy — for years.</p>
            <p>You&apos;ve saved a hundred TikToks.</p>
            <p>You&apos;ve cried in the shower.</p>
            <p className="mm-identify-punch">
              And you still don&apos;t recognize the woman in the mirror.
            </p>
            <p className="mm-identify-close">
              I know. I was her.
            </p>
          </div>
        </div>
      </section>

      {/* Cost of staying stuck — Pain before Pleasure */}
      <section className="mm-cost" data-testid="mm-cost">
        <div className="container mm-narrow">
          <h2 className="mm-h2">
            Here&apos;s what nobody tells you<br />
            <em>about staying like this.</em>
          </h2>
          <ul className="mm-cost-list">
            <li>
              <strong>Your body keeps score.</strong>
              It stays tight in your shoulders, heavy in your gut, tired in your bones — even after you sleep.
            </li>
            <li>
              <strong>Your kids don&apos;t remember your resolutions.</strong>
              They remember your energy. When you&apos;re depleted, they feel it. Every single day.
            </li>
            <li>
              <strong>You spend money over and over on the same class, the same book, the same coach.</strong>
              Because none of them touched the actual thing that&apos;s keeping you stuck.
            </li>
            <li>
              <strong>Another year passes.</strong>
              And you&apos;re still promising yourself you&apos;ll start Monday.
            </li>
            <li>
              <strong>The woman who used to laugh loud, dance freely, feel like herself.</strong>
              She doesn&apos;t come back on her own.
            </li>
          </ul>
        </div>
      </section>

      {/* Name the Enemy */}
      <section className="mm-enemy" data-testid="mm-enemy">
        <div className="container">
          <div className="mm-section-header">
            <span className="mm-eyebrow"><Shield size={14} /> The Noise</span>
            <h2 className="mm-h2">
              The noise you&apos;ve been drowning in.
            </h2>
            <p className="mm-section-sub">
              None of these are working. Here&apos;s why.
            </p>
          </div>
          <div className="mm-enemy-grid">
            <div className="mm-enemy-card">
              <span className="mm-enemy-x">✕</span>
              <h3>The Quick Fix</h3>
              <p>Green-juice cleanses. 30-day resets. Instagram-perfect breakfast reels.</p>
              <p className="mm-enemy-verdict">They cost you money and shame — and vanish by month two.</p>
            </div>
            <div className="mm-enemy-card">
              <span className="mm-enemy-x">✕</span>
              <h3>The Endless Talk</h3>
              <p>Sitting on the same couch for four years. Understanding your pattern. Still stuck in it.</p>
              <p className="mm-enemy-verdict">Insight isn&apos;t a shift. Talking about it isn&apos;t healing the body.</p>
            </div>
            <div className="mm-enemy-card">
              <span className="mm-enemy-x">✕</span>
              <h3>The Trend Cycle</h3>
              <p>Carnivore. Plant-based. Fasting. Anti-inflammatory. A new answer every 6 weeks.</p>
              <p className="mm-enemy-verdict">Your body doesn&apos;t need another trend. It needs the basics — kindly, consistently.</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Mechanism — Momshiach Method */}
      <section id="the-method" className="mm-mechanism" data-testid="mm-mechanism">
        <div className="container">
          <div className="mm-section-header">
            <span className="mm-eyebrow"><Sparkles size={14} /> The Mechanism</span>
            <h2 className="mm-h2">
              The <em>Momshiach Method™</em>
            </h2>
            <p className="mm-section-sub">
              A three-part protocol that meets your body, your routine, and your nervous system — all at once.<br />
              This is why it holds where everything else slipped.
            </p>
          </div>
          <div className="mm-pillars">
            <div className="mm-pillar">
              <div className="mm-pillar-num">01</div>
              <Zap size={26} />
              <h3>Body</h3>
              <p className="mm-pillar-sub">6 Energy Therapy Sessions</p>
              <p>Remote. Passive. You don&apos;t need to be present. I clear the energetic weight your body has been holding — the stuff you can&apos;t name but always feel.</p>
            </div>
            <div className="mm-pillar mm-pillar-highlight">
              <div className="mm-pillar-num">02</div>
              <Heart size={26} />
              <h3>Routine</h3>
              <p className="mm-pillar-sub">3 Health Coaching Sessions</p>
              <p>Kosher, plant-forward, non-restrictive. One small habit at a time — the kind that actually stays after we stop meeting.</p>
            </div>
            <div className="mm-pillar">
              <div className="mm-pillar-num">03</div>
              <Repeat size={26} />
              <h3>Release</h3>
              <p className="mm-pillar-sub">3 EFT / Choice Sessions</p>
              <p>You pick the sessions that meet you where you are — EFT tapping, narrative work, or personal growth support.</p>
            </div>
          </div>
          <p className="mm-mechanism-plus">
            <Sparkles size={14} /> Plus my full library: <em>Nourish Yourself First</em> cookbook, video companion, Fridge Magic course, and every workbook I&apos;ve made — yours for a full year.
          </p>
        </div>
      </section>

      {/* Value Stack */}
      <section className="mm-stack" data-testid="mm-stack">
        <div className="container mm-narrow">
          <div className="mm-section-header">
            <span className="mm-eyebrow"><Star size={14} /> Everything Inside</span>
            <h2 className="mm-h2">Here&apos;s what&apos;s in your hands.</h2>
          </div>
          <ul className="mm-stack-list">
            <li>
              <div><strong>6 Energy Therapy Sessions</strong><span>Remote · passive · voice-note recap after each</span></div>
              <div className="mm-stack-price">3,570 NIS</div>
            </li>
            <li>
              <div><strong>3 Health Coaching Sessions</strong><span>Integrative Nutrition · one habit at a time</span></div>
              <div className="mm-stack-price">1,800 NIS</div>
            </li>
            <li>
              <div><strong>3 EFT / Choice Sessions</strong><span>Your call — EFT, narrative therapy, or personal growth</span></div>
              <div className="mm-stack-price">1,500 NIS</div>
            </li>
            <li>
              <div><strong><em>Nourish Yourself First</em> Cookbook + Video Companion</strong><span>Full plant-forward foundation</span></div>
              <div className="mm-stack-price">250 NIS</div>
            </li>
            <li>
              <div><strong>Fridge Magic Course + Digital Library</strong><span>Every workbook and course I&apos;ve created</span></div>
              <div className="mm-stack-price">550 NIS</div>
            </li>
            <li className="mm-stack-total">
              <div><strong>Individual value</strong><span>What each piece costs on its own</span></div>
              <div className="mm-stack-price mm-stack-strike">7,670 NIS</div>
            </li>
            <li className="mm-stack-final">
              <div><strong>Your investment</strong><span>Everything together, in one place</span></div>
              <div className="mm-stack-price mm-stack-gold">5,497 NIS</div>
            </li>
          </ul>
          <p className="mm-stack-save">You save <strong>2,173 NIS</strong> — and get the three things that actually work, held together.</p>
        </div>
      </section>

      {/* The Investment — Payment Options + Single CTA */}
      <section className="mm-invest" data-testid="mm-invest" id="claim">
        <div className="container mm-narrow">
          <div className="mm-invest-card">
            <span className="mm-invest-tag"><Sparkles size={14} /> Signature Offer</span>
            <h2 className="mm-h2">Choose the softest landing.</h2>
            <p className="mm-invest-sub">Same offer. Different rhythm. You pick.</p>

            <div className="mm-payment-options">
              <button
                onClick={() => openCheckout('full')}
                className="mm-payment-card mm-payment-card-primary"
                data-testid="cta-pay-full"
              >
                <span className="mm-payment-badge">Best price</span>
                <div className="mm-payment-title">Pay in Full</div>
                <div className="mm-payment-amount">5,497 <span>NIS</span></div>
                <div className="mm-payment-sub">One payment · save 200 NIS vs installments</div>
              </button>
              <button
                onClick={() => openCheckout('3pay')}
                className="mm-payment-card"
                data-testid="cta-pay-3"
              >
                <div className="mm-payment-title">3 Monthly Payments</div>
                <div className="mm-payment-amount">1,899 <span>NIS/mo</span></div>
                <div className="mm-payment-sub">5,697 NIS total</div>
              </button>
              <button
                onClick={() => openCheckout('6pay')}
                className="mm-payment-card"
                data-testid="cta-pay-6"
              >
                <div className="mm-payment-title">6 Monthly Payments</div>
                <div className="mm-payment-amount">999 <span>NIS/mo</span></div>
                <div className="mm-payment-sub">5,994 NIS total</div>
              </button>
            </div>

            <button
              onClick={() => openCheckout('full')}
              className="btn-primary btn-lg mm-primary-cta"
              data-testid="cta-claim-your-spot"
            >
              <span>Claim Your Spot</span>
              <ArrowRight size={18} />
            </button>
            <p className="mm-invest-legal">
              <Shield size={12} /> Secure PayPal checkout (card or PayPal) · welcome email from Chanah within 24 hours
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof — near the CTA */}
      <section className="mm-proof" data-testid="mm-proof">
        <div className="container">
          <div className="mm-section-header">
            <span className="mm-eyebrow"><Star size={14} /> What Moms Say</span>
            <h2 className="mm-h2">You are not the first to feel this.<br /><em>You won&apos;t be the last to come back.</em></h2>
          </div>
          <div className="mm-proof-grid">
            {testimonials.slice(0, 3).map((t) => (
              <figure key={t.id} className="mm-proof-card">
                <div className="mm-proof-stars">
                  {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={14} fill="currentColor" />)}
                </div>
                <blockquote>&ldquo;{t.text}&rdquo;</blockquote>
                <figcaption>— {t.name}</figcaption>
              </figure>
            ))}
          </div>
          <div className="mm-proof-cta-wrap">
            <button onClick={() => openCheckout('full')} className="btn-primary btn-lg" data-testid="cta-claim-your-spot-mid">
              <span>Claim Your Spot</span>
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mm-faq" data-testid="mm-faq">
        <div className="container mm-narrow">
          <div className="mm-section-header">
            <span className="mm-eyebrow">Questions</span>
            <h2 className="mm-h2">The things you&apos;re quietly wondering.</h2>
          </div>
          <div className="mm-faq-list">
            {faqs.map((f, i) => (
              <div key={i} className={`mm-faq-item ${openFaq === i ? 'open' : ''}`}>
                <button
                  className="mm-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`faq-toggle-${i}`}
                >
                  <span>{f.q}</span>
                  <ChevronDown size={18} />
                </button>
                {openFaq === i && <div className="mm-faq-a">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mm-final" data-testid="mm-final">
        <div className="container mm-narrow">
          <h2 className="mm-h2 mm-final-title">
            The version of you that laughs loud,<br />
            eats what nourishes her, moves through her days grounded —<br />
            <em>she&apos;s still in there.</em>
          </h2>
          <p className="mm-final-sub">
            She just needs someone to hold the weight for a minute while she remembers how to breathe.
          </p>
          <button
            onClick={() => openCheckout('full')}
            className="btn-primary btn-lg mm-final-cta"
            data-testid="cta-claim-your-spot-final"
          >
            <span>Claim Your Spot</span>
            <ArrowRight size={18} />
          </button>
          <p className="mm-final-signature">— Chanah Yael</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <img src="/brand/logo.png" alt="Momshiach" style={{ height: 60, filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
              <p className="body-small" style={{ marginTop: 8 }}>Your Personal Redemption</p>
            </div>
            <div className="footer-section">
              <h4 className="body-medium">Contact</h4>
              <div className="footer-contact">
                <a href="https://wa.me/972529326458" className="footer-link" target="_blank" rel="noopener noreferrer">
                  <Phone size={16} />
                  <span>+972-52-932-6458</span>
                </a>
              </div>
            </div>
            <div className="footer-section">
              <h4 className="body-medium">Explore</h4>
              <Link to="/energy-therapy" className="footer-link">Energy Therapy</Link>
              <Link to="/carebnb" className="footer-link" data-testid="footer-carebnb-link">CareBnB Getaways</Link>
              <Link to="/health-coaching" className="footer-link">Health Coaching</Link>
              <Link to="/gift" className="footer-link">Gift a Getaway</Link>
              <Link to="/resources" className="footer-link" data-testid="footer-resources-link">Resources & Courses</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="caption">© 2026 Momshiach. All rights reserved. Techniques taught under Rabbi Ben-Tzion Wosner shlit&quot;a.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Method;
